import { useCallback, useMemo } from "react";
import { useDeviceType } from "@b2bportal/core-utilities";
import type { LodgingMediaAsset } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { htmlDecode } from "@hopper-b2b/utilities";
import { useControlledDialog } from "@tenant/ui";
import type { ImageGalleryComponentProps } from "./ImageGallery";

export enum MinimumImage {
  one = "minimum-image-one",
  two = "minimum-image-two",
  three = "minimum-image-three",
}
export const useImageGallery = ({
  name,
  media,
}: ImageGalleryComponentProps) => {
  const { t } = useI18nContext();

  const { isDesktopAndUp } = useDeviceType();

  const imageGalleryDialog = useControlledDialog();

  //TODO: need to understand behavior from design
  const imageColumns: MinimumImage | null = useMemo(() => {
    const length = media.length;

    if (length === 1) return MinimumImage.one;
    else if (length === 2) return MinimumImage.two;
    else if (length >= 3) return MinimumImage.three;
    else return null;
  }, [media.length]);

  const showCaseImages = useMemo(() => {
    return media.slice(0, isDesktopAndUp ? 3 : 1);
  }, [media, isDesktopAndUp]);

  const getAltText = useCallback(
    (image: LodgingMediaAsset) => {
      const category = "category" in image ? image.category : undefined;
      return category != null
        ? t("core-hotels.image.altWithCategory", {
            category: category,
          })
        : t("core-hotels.image.altWithNoCategory");
    },
    [t]
  );

  return useMemo(() => {
    return {
      context: {
        imageColumns,
        showCaseImages,
        images: media,
        imageGalleryDialog,
      },
      text: {
        dialogTitle: htmlDecode(
          t("core-hotels.image.imageGallery.title", {
            hotelName: name ?? "",
          })
        ),
        getAltText,
        imageButton: t("core-hotels.image.showAllImages"),
      },
    };
  }, [
    imageColumns,
    showCaseImages,
    media,
    imageGalleryDialog,
    t,
    name,
    getAltText,
  ]);
};
